import { Card } from "~components";
import { getImageSrc } from "~lib";
import { EventsQuery, ExtendedFile } from "~graphql/sdk";
import {
  Box,
  PrimaryButton,
  SecondaryButton,
  Text,
  LegacyText,
} from "flicket-ui";
import { MembershipsQuery } from "~graphql/typed-document-nodes";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import {
  AttributeNames,
  InteractionNames,
} from "~telemetry/enums/AttributeNames";
import { useRouter } from "next/router";
import { formatEventDateRange } from "~lib/helpers/formatDate";
import { useOrganization } from "~hooks";

type TourEventTileProps = {
  title: string;
  id: string;
  startDate: string;
  thumbnail: ExtendedFile;
  venue:
    | EventsQuery["events"]["edges"][number]["node"]["venue"]
    | MembershipsQuery["memberships"]["edges"][number]["node"]["venue"];
  hasPublicRelease?: boolean;
  isWaitlistAvailable?: boolean;
  type: "event" | "membership";
};

export const TourTile = ({
  title,
  id,
  startDate,
  thumbnail,
  venue,
  hasPublicRelease,
  isWaitlistAvailable,
  type,
}: TourEventTileProps) => {
  const router = useRouter();
  const { organization, t } = useOrganization();

  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId: type === "event" && id,
    pageName: "event-tour-tile",
    attributes: {
      [AttributeNames.IS_MEMBERSHIP]: type === "membership",
    },
  });

  let showPrimaryEventButton =
    type === "event" && (hasPublicRelease || isWaitlistAvailable);

  let primaryEventButtonLabel = hasPublicRelease
    ? "Buy tickets"
    : "Join the waitlist";

  // ASB Classic
  if (
    organization.id === "c8f276b9-87d5-42c8-ba3f-102089545cde" &&
    !hasPublicRelease
  ) {
    primaryEventButtonLabel = "Join the club";
  }

  // warriors.flicket.io
  if (organization.id === "6e68eb68-bb0c-494b-9e61-06b65c1342e9") {
    showPrimaryEventButton = false;
  }

  return (
    <Box p="6/4" width={{ _: "100%", sm: "50%", md: "33.33%" }} key={id}>
      <Card minHeight="100%" image={getImageSrc(thumbnail)}>
        <LegacyText
          fontSize={4}
          lineHeight="medium"
          fontWeight="heavy"
          variant="uppercase"
          letterSpacing={"-0.17px" as any}
          mb={1}
        >
          {`${title}`}
        </LegacyText>

        {venue.address && (
          <LegacyText
            fontSize={4}
            lineHeight="medium"
            letterSpacing={"-0.17px" as any}
            mb={1}
          >
            {venue.name}, {venue.address.city}, {venue.address.country}
          </LegacyText>
        )}

        {startDate && (
          <LegacyText
            fontSize={3}
            lineHeight="medium"
            letterSpacing={"-0.17px" as any}
            mb={1}
          >
            {formatEventDateRange(startDate, startDate, {
              format: "basicShort",
              timeZone: venue?.timezone,
              locale: venue?.locale,
            })}
          </LegacyText>
        )}

        <SecondaryButton
          mt={2}
          fontSize={4}
          w="100%"
          boxShadow="button"
          href={`/${type === "event" ? "events" : "memberships"}/${id}`}
        >
          {type === "event" ? "Event" : t("membership_name")} Information
        </SecondaryButton>

        {showPrimaryEventButton && (
          <PrimaryButton
            mt={2}
            fontSize={4}
            w="100%"
            boxShadow="button"
            onClick={captureClick(
              () => {
                void router.push(
                  hasPublicRelease
                    ? `/events/${id}/reservation`
                    : `/events/${id}/registration`
                );
              },
              InteractionNames.BUTTON_PRIMARY,
              {
                [AttributeNames.BUTTON_LABEL]: primaryEventButtonLabel,
              }
            )}
          >
            {primaryEventButtonLabel}
          </PrimaryButton>
        )}

        {type === "membership" && (
          <PrimaryButton
            mt={2}
            fontSize={4}
            w="100%"
            boxShadow="button"
            onClick={captureClick(
              () => {
                void router.push(`/memberships/${id}/reservation`);
              },
              InteractionNames.BUTTON_PRIMARY,
              {
                [AttributeNames.BUTTON_LABEL]: "Buy memberships",
              }
            )}
          >
            {`Buy ${t("membership_name").toLowerCase()}`}
          </PrimaryButton>
        )}
      </Card>
    </Box>
  );
};
